/**
 * Dates, competências and the fiscal month — §5.6, BR-03, BR-21.
 *
 * 🔒 **The fiscal month is NOT the calendar month.** Broker contracts commonly run
 * day 20 to day 19 of the following month. Every period view has to render the
 * configured range, and code that reaches for "first day of the month" is wrong on
 * this platform by default.
 *
 * All dates on the wire are `YYYY-MM-DD` strings and all competências `YYYY-MM`
 * (§7). They are parsed as **calendar values, never as instants** — `new Date('2026-08-05')`
 * is parsed by JS as UTC midnight and renders as 04/08 anywhere west of Greenwich,
 * which is every user this platform has. That single behaviour is why the parsing
 * below is by hand rather than by `Date` constructor.
 */

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const

/** A calendar date, free of timezone. */
export interface DataCivil {
  ano: number
  /** 1–12, as humans write it — not JS's 0–11. */
  mes: number
  dia: number
}

/**
 * Parses `YYYY-MM-DD` without going through `Date`.
 *
 * ⚠️ `new Date('2026-08-05')` is UTC midnight, which in `America/Sao_Paulo`
 * (UTC−3) formats as **04/08/2026** — a date shown one day early, everywhere,
 * silently. The platform's dates are civil dates on a contract, not instants.
 */
export function paraDataCivil(iso: string): DataCivil | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!partes) return null
  const [, ano, mes, dia] = partes
  return { ano: Number(ano), mes: Number(mes), dia: Number(dia) }
}

function dois(valor: number): string {
  return String(valor).padStart(2, '0')
}

/** `05/08/2026`. Returns an em dash for null — the "no date yet" of §7. */
export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—'
  const data = paraDataCivil(iso)
  if (!data) return '—'
  return `${dois(data.dia)}/${dois(data.mes)}/${data.ano}`
}

/** `5 de agosto de 2026` — for prose, not tables. */
export function formatarDataPorExtenso(iso: string | null | undefined): string {
  if (!iso) return '—'
  const data = paraDataCivil(iso)
  if (!data) return '—'
  return `${data.dia} de ${MESES[data.mes - 1]} de ${data.ano}`
}

/** `agosto/2026`, from a `YYYY-MM` competência. */
export function formatarCompetencia(competencia: string): string {
  const partes = /^(\d{4})-(\d{2})$/.exec(competencia)
  if (!partes) return '—'
  const [, ano, mes] = partes
  const indice = Number(mes) - 1
  const nome = MESES[indice]
  return nome ? `${nome}/${ano}` : '—'
}

/** `05/08/2026 – 19/09/2026`. A null end means open-ended, not missing. */
export function formatarPeriodo(inicioIso: string, fimIso: string | null): string {
  return fimIso
    ? `${formatarData(inicioIso)} – ${formatarData(fimIso)}`
    : `desde ${formatarData(inicioIso)}`
}

export interface MesFiscal {
  inicio: DataCivil
  fim: DataCivil
  /** `20/07/2026 – 19/08/2026` */
  rotulo: string
}

/**
 * The fiscal month containing a reference date — **BR-03**.
 *
 * `inicioDia = 20` means the cycle runs day 20 to day 19 of the next month. The
 * reference decides WHICH cycle: 25/07 is inside 20/07–19/08, but 05/07 is still
 * inside 20/06–19/07.
 *
 * 🔒 **Never assume a calendar boundary.** `inicioDia = 1` is a legitimate
 * configuration and produces the calendar month, but that is one case of the rule,
 * not the default the others deviate from.
 *
 * A cycle starting on the 31st is clamped to the last real day of the month —
 * February has no 31st, and a contract that says 31 means "end of month".
 */
export function calcularMesFiscal(inicioDia: number, referenciaIso: string): MesFiscal | null {
  const referencia = paraDataCivil(referenciaIso)
  if (!referencia || inicioDia < 1 || inicioDia > 31) return null

  // Which cycle the reference falls in: on or after the start day, the cycle began
  // this month; before it, the cycle began last month.
  const comecouEsteMes = referencia.dia >= diaValido(inicioDia, referencia.ano, referencia.mes)

  const { ano: anoInicio, mes: mesInicio } = comecouEsteMes
    ? { ano: referencia.ano, mes: referencia.mes }
    : mesAnterior(referencia.ano, referencia.mes)

  const inicio: DataCivil = {
    ano: anoInicio,
    mes: mesInicio,
    dia: diaValido(inicioDia, anoInicio, mesInicio),
  }

  // The cycle ends the day before the next one starts.
  const proximo = mesSeguinte(anoInicio, mesInicio)
  const diaInicioProximo = diaValido(inicioDia, proximo.ano, proximo.mes)
  const fim = diaAnterior({ ano: proximo.ano, mes: proximo.mes, dia: diaInicioProximo })

  return {
    inicio,
    fim,
    rotulo: `${dois(inicio.dia)}/${dois(inicio.mes)}/${inicio.ano} – ${dois(fim.dia)}/${dois(fim.mes)}/${fim.ano}`,
  }
}

function diasNoMes(ano: number, mes: number): number {
  // Day 0 of the next month is the last day of this one. Uses UTC so the result
  // cannot shift with the machine's timezone.
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate()
}

/** Clamps a configured start day to a day that exists in the given month. */
function diaValido(dia: number, ano: number, mes: number): number {
  return Math.min(dia, diasNoMes(ano, mes))
}

function mesAnterior(ano: number, mes: number): { ano: number; mes: number } {
  return mes === 1 ? { ano: ano - 1, mes: 12 } : { ano, mes: mes - 1 }
}

function mesSeguinte(ano: number, mes: number): { ano: number; mes: number } {
  return mes === 12 ? { ano: ano + 1, mes: 1 } : { ano, mes: mes + 1 }
}

function diaAnterior(data: DataCivil): DataCivil {
  if (data.dia > 1) return { ...data, dia: data.dia - 1 }
  const anterior = mesAnterior(data.ano, data.mes)
  return { ...anterior, dia: diasNoMes(anterior.ano, anterior.mes) }
}

/** `20/07/2026 – 19/08/2026`, or an em dash if the configuration is nonsense. */
export function formatarMesFiscal(inicioDia: number, referenciaIso: string): string {
  return calcularMesFiscal(inicioDia, referenciaIso)?.rotulo ?? '—'
}

/**
 * `12/03/2026` a partir de um INSTANTE ISO (`2026-03-12T13:00:00Z`), no fuso de quem
 * lê — o "enviado em" de um upload é um momento, não uma data civil de contrato.
 */
export function formatarDataDeInstante(iso: string | null | undefined): string {
  if (!iso) return '—'
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return '—'
  return data.toLocaleDateString('pt-BR')
}

/**
 * `12/03/2026 às 14:05` — mesmo instante de `formatarDataDeInstante`, com a hora. Para
 * quando SÓ a data não prova que algo aconteceu — "verificado em" de um clique que pode
 * se repetir várias vezes no mesmo dia precisa da hora para o usuário confirmar que o
 * clique de agora é o que está refletido na tela, não um de mais cedo.
 */
export function formatarDataHoraDeInstante(iso: string | null | undefined): string {
  if (!iso) return '—'
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return '—'
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${data.toLocaleDateString('pt-BR')} às ${hora}`
}
